import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as d3 from 'd3';

// Types pour les données de formation
interface FormationItem {
  id: number;
  title: string;
  platform: string;
  category: string;
  date: string;
  skills: string[];
  description: string;
  certificateUrl?: string;
}

// Type pour les catégories
interface Category {
  id: number;
  name: string;
  color: string;
}

const FormationsContainer = styled.div`
  padding: 8rem 2rem 6rem;
  min-height: calc(100vh - 14rem);
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.accent});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 800px;
  margin: 0 auto;
  gap: 2rem;
`;

// Conteneur pour le filtre des catégories
const FilterContainer = styled.div`
  background-color: ${({ theme }) => `${theme.colors.backgroundLight}cc`};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
  margin-bottom: 1rem;
`;

const FilterTitle = styled.h3`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 1rem 0;
  text-align: center;
`;

const CategoriesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
`;

const CategoryButton = styled.button<{ color: string; active: boolean }>`
  display: flex;
  align-items: center;
  background-color: ${({ theme, active, color }) => 
    active ? `${color}22` : `${theme.colors.background}99`};
  border: 1px solid ${({ active, color }) => 
    active ? color : 'rgba(255, 255, 255, 0.2)'};
  color: ${({ theme, active, color }) => 
    active ? color : theme.colors.textSecondary};
  padding: 0.6rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  }
  
  &:focus {
    outline: none;
  }
`;

const CategoryDot = styled.span<{ color: string }>`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.color};
  margin-right: 8px;
`;

const ResetFilterButton = styled.button`
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.textSecondary};
  color: ${({ theme }) => theme.colors.textSecondary};
  border-radius: 20px;
  padding: 0.6rem 1.2rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  align-self: center;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    color: white;
    transform: translateY(-2px);
  }
`;

// Liste des formations
const FormationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

// Élément de formation individuel (accordéon)
const FormationItem = styled(motion.div)`
  background-color: ${({ theme }) => `${theme.colors.backgroundLight}cc`};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const FormationHeader = styled.div<{ color: string }>`
  position: relative;
  padding: 1.2rem;
  cursor: pointer;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1rem;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: ${props => props.color};
  }
`;

const CategoryBadge = styled.div<{ color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${props => `${props.color}22`};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::after {
    content: '';
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background-color: ${props => props.color};
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormationTitle = styled.h3`
  font-size: 1.1rem;
  margin: 0 0 0.3rem 0;
  color: ${({ theme }) => theme.colors.text};
`;

const FormationPlatform = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  
  svg {
    width: 14px;
    height: 14px;
    margin-right: 6px;
    fill: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const ToggleButton = styled.button<{ isOpen: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background-color: ${({ theme }) => `${theme.colors.background}dd`};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.2rem;
  line-height: 1;
  padding: 0;
  transform: ${props => props.isOpen ? 'rotate(0)' : 'rotate(0)'};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  &:focus {
    outline: none;
  }
`;

// Contenu détaillé de la formation (partie dépliable)
const FormationDetails = styled(motion.div)`
  padding: 0;
  overflow: hidden;
`;

const DetailsContent = styled.div`
  padding: 0 1.2rem 1.2rem 1.2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const FormationDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 1rem 0;
  color: ${({ theme }) => theme.colors.text};
`;

const SkillsTitle = styled.h4`
  font-size: 0.9rem;
  margin: 1.5rem 0 0.8rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SkillTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0 1rem;
`;

const SkillTag = styled.span<{ color: string }>`
  background-color: ${({ color }) => `${color}15`};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ color }) => `${color}30`};
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
`;

const DateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  svg {
    width: 14px;
    height: 14px;
    margin-right: 6px;
    fill: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const CertificateButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  margin-top: 1rem;
  cursor: pointer;
  text-decoration: none;
  
  svg {
    margin-right: 0.5rem;
    width: 16px;
    height: 16px;
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.gradientStart};
  }
`;

const NoFormationsMessage = styled.div`
    text-align: center;
    padding: 2rem;
    background-color: ${({ theme }) => `${theme.colors.backgroundLight}cc`};
    border-radius: 12px;
    box-shadow: ${({ theme }) => theme.shadows.sm};
    
    h3 {
      color: ${({ theme }) => theme.colors.textSecondary};
      margin-bottom: 1rem;
    }
    
    p {
      color: ${({ theme }) => theme.colors.textSecondary};
      font-size: 0.9rem;
    }
  `;

  // Animation variants
const listItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  exit: { opacity: 0, y: -5, transition: { duration: 0.2 } }
};

const contentVariants = {
  collapsed: { 
    height: 0, 
    opacity: 0,
    transition: {
      height: { duration: 0.3, ease: "easeInOut" },
      opacity: { duration: 0.2 }
    }
  },
  expanded: { 
    height: 'auto', 
    opacity: 1, 
    transition: { 
      height: { duration: 0.4, ease: "easeInOut" },
      opacity: { duration: 0.3, delay: 0.1 }
    } 
  }
};

// Fonction pour formater les dates selon la langue et la taille d'écran
const formatDate = (dateString: string, language: string, isMobile = false): string => {
  // Si pas de date, retourner un message approprié
  if (!dateString) {
    return language === 'fr' ? "Non spécifié" : "Not specified";
  }
  
  // Format des dates stockées "MM-YYYY"
  const [month, year] = dateString.split('-');
  
  if (language === 'fr') {
    // Format compact pour mobile: MM/YYYY
    if (isMobile) {
      return `${month}/${year}`;
    }
    
    // Format français standard: mois année (exemple: "janvier 2024")
    const monthNames = [
      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
    ];
    // Soustraire 1 car les mois sont stockés de 01 à 12, mais les indices du tableau vont de 0 à 11
    const monthIndex = parseInt(month, 10) - 1;
    if (monthIndex >= 0 && monthIndex < 12) {
      return `${monthNames[monthIndex]} ${year}`;
    } else {
      console.warn(`Invalid month index: ${monthIndex} from date: ${dateString}`);
      return `${month}/${year}`; // Fallback
    }
  } else {
    // Format anglais: YYYY/MM
    return `${year}/${month}`;
  }
};

const FormationsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Détection de la largeur de l'écran
  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Fonction pour déterminer si l'affichage doit être compact
  const isCompactView = useCallback(() => {
    return windowWidth < 768; // Mobile breakpoint
  }, [windowWidth]);
  
  // Données d'exemple pour les formations
  const formationData: FormationItem[] = [
    {
      id: 1,
      title: t('education:items.1.title'),
      platform: t('education:items.1.platform'),
      category: t('education:categories.dataScience'),
      date: "02-2024",
      skills: t('education:items.1.skills', { returnObjects: true }) as string[],
      description: t('education:items.1.description'),
      certificateUrl: ""
    },
    {
      id: 2,
      title: t('education:items.2.title'),
      platform: t('education:items.2.platform'),
      category: t('education:categories.dataScience'),
      date: "01-2024",
      skills: t('education:items.2.skills', { returnObjects: true }) as string[],
      description: t('education:items.2.description'),
      certificateUrl: ""
    },
    {
      id: 3,
      title: t('education:items.3.title'),
      platform: t('education:items.3.platform'),
      category: t('education:categories.dataScience'),
      date: "01-2024",
      skills: t('education:items.3.skills', { returnObjects: true }) as string[],
      description: t('education:items.3.description'),
      certificateUrl: ""
    },
    {
      id: 4,
      title: t('education:items.4.title'),
      platform: t('education:items.4.platform'),
      category: t('education:categories.misc'),
      date: "12-2023",
      skills: t('education:items.4.skills', { returnObjects: true }) as string[],
      description: t('education:items.4.description'),
      certificateUrl: ""
    },
    {
      id: 5,
      title: t('education:items.5.title'),
      platform: t('education:items.5.platform'),
      category: t('education:categories.misc'),
      date: "11-2023",
      skills: t('education:items.5.skills', { returnObjects: true }) as string[],
      description: t('education:items.5.description'),
      certificateUrl: ""
    },
    {
      id: 6,
      title: t('education:items.6.title'),
      platform: t('education:items.6.platform'),
      category: t('education:categories.mlops'),
      date: "10-2023",
      skills: t('education:items.6.skills', { returnObjects: true }) as string[],
      description: t('education:items.6.description'),
      certificateUrl: ""
    },
    {
      id: 7,
      title: t('education:items.7.title'),
      platform: t('education:items.7.platform'),
      category: t('education:categories.softSkills'),
      date: "09-2023",
      skills: t('education:items.7.skills', { returnObjects: true }) as string[],
      description: t('education:items.7.description'),
      certificateUrl: ""
    },
    {
      id: 8,
      title: t('education:items.8.title'),
      platform: t('education:items.8.platform'),
      category: t('education:categories.softSkills'),
      date: "08-2023",
      skills: t('education:items.8.skills', { returnObjects: true }) as string[],
      description: t('education:items.8.description'),
      certificateUrl: ""
    },
    {
      id: 9,
      title: t('education:items.9.title'),
      platform: t('education:items.9.platform'),
      category: t('education:categories.softSkills'),
      date: "07-2023",
      skills: t('education:items.9.skills', { returnObjects: true }) as string[],
      description: t('education:items.9.description'),
      certificateUrl: ""
    },
    {
      id: 10,
      title: t('education:items.10.title'),
      platform: t('education:items.10.platform'),
      category: t('education:categories.misc'),
      date: "06-2023",
      skills: t('education:items.10.skills', { returnObjects: true }) as string[],
      description: t('education:items.10.description'),
      certificateUrl: ""
    },
    {
      id: 11,
      title: t('education:items.11.title'),
      platform: t('education:items.11.platform'),
      category: t('education:categories.softSkills'),
      date: "05-2023",
      skills: t('education:items.11.skills', { returnObjects: true }) as string[],
      description: t('education:items.11.description'),
      certificateUrl: ""
    }
  ];
  
  // Définition des catégories et leurs couleurs
  const categories: Category[] = [
    { id: 1, name: t('education:categories.dataScience'), color: "#4285F4" },
    { id: 2, name: t('education:categories.softSkills'), color: "#EA4335" },
    { id: 3, name: t('education:categories.mlops'), color: "#FBBC05" },
    { id: 4, name: t('education:categories.misc'), color: "#34A853" }
  ];
  
  // Filtrer les formations selon la catégorie active
  const filteredFormations = activeCategory 
    ? formationData.filter(formation => formation.category === activeCategory)
    : formationData;
  
  // Trier les formations par date (plus récentes en premier)
  const sortedFormations = [...filteredFormations].sort((a, b) => {
    // Si pas de date, placer à la fin
    if (!a.date) return 1;
    if (!b.date) return -1;
    
    // Sinon, trier par date décroissante (plus récent en premier)
    return b.date.localeCompare(a.date);
  });
  
  // Gérer le clic sur une catégorie
  const handleCategoryClick = (category: string) => {
    if (activeCategory === category) {
      setActiveCategory(null); // Désactiver le filtre
    } else {
      setActiveCategory(category); // Activer le filtre
    }
    setExpandedId(null); // Refermer tous les accordéons lors du changement de filtre
  };
  
  // Basculer l'état ouvert/fermé d'un élément
  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };
  
  // Trouver la couleur correspondant à une catégorie
  const getCategoryColor = (categoryName: string): string => {
    const category = categories.find(cat => cat.name === categoryName);
    return category ? category.color : "#999";
  };
  
  return (
    <FormationsContainer>
      <SectionTitle
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {t('education:pageTitle')}
      </SectionTitle>
      
      <ContentWrapper>
        {/* Filtres de catégories */}
        <FilterContainer>
          <FilterTitle>{t('education:filterTitle')}</FilterTitle>
          <CategoriesWrapper>
            {categories.map((category) => (
              <CategoryButton 
                key={category.id}
                color={category.color}
                active={activeCategory === category.name}
                onClick={() => handleCategoryClick(category.name)}
              >
                <CategoryDot color={category.color} />
                {category.name}
              </CategoryButton>
            ))}
            
            {activeCategory && (
              <CategoryButton 
                color="#ffffff"
                active={false}
                onClick={() => setActiveCategory(null)}
              >
                {t('education:showAll')}
              </CategoryButton>
            )}
          </CategoriesWrapper>
        </FilterContainer>
        
        {/* Liste des formations */}
        <AnimatePresence mode="sync">
          {sortedFormations.length > 0 ? (
            <FormationsList>
              {sortedFormations.map((formation) => {
                const categoryColor = getCategoryColor(formation.category);
                const isExpanded = expandedId === formation.id;
                
                return (
                  <FormationItem 
                    key={formation.id}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={listItemVariants}
                  >
                    <FormationHeader 
                      color={categoryColor}
                      onClick={() => toggleExpand(formation.id)}
                    >
                      <CategoryBadge color={categoryColor} />
                      
                      <HeaderContent>
                        <FormationTitle>{formation.title}</FormationTitle>
                        <FormationPlatform>
                          <svg viewBox="0 0 24 24">
                            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                          </svg>
                          {formation.platform}
                        </FormationPlatform>
                      </HeaderContent>
                      
                      <ToggleButton isOpen={isExpanded}>
                        {isExpanded ? '-' : '+'}
                      </ToggleButton>
                    </FormationHeader>
                    
                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <FormationDetails
                          key={`details-${formation.id}`}
                          initial="collapsed"
                          animate="expanded"
                          exit="collapsed"
                          variants={contentVariants}
                        >
                          <DetailsContent>
                            <FormationDescription>
                              {formation.description}
                            </FormationDescription>
                            
                            {formation.date && (
                              <DateBadge>
                                <svg viewBox="0 0 24 24">
                                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                                </svg>
                                {t('education:completedOn')} {formatDate(formation.date, i18n.language, isCompactView())}
                              </DateBadge>
                            )}
                            
                            <SkillsTitle>{t('education:skillsAcquired')}</SkillsTitle>
                            <SkillTags>
                              {formation.skills.map((skill, index) => (
                                <SkillTag key={index} color={categoryColor}>
                                  {skill}
                                </SkillTag>
                              ))}
                            </SkillTags>
                            
                            {formation.certificateUrl && (
                              <CertificateButton 
                                href={formation.certificateUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                <svg viewBox="0 0 24 24">
                                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                </svg>
                                {t('education:viewCertificate')}
                              </CertificateButton>
                            )}
                          </DetailsContent>
                        </FormationDetails>
                      )}
                    </AnimatePresence>
                  </FormationItem>
                );
              })}
            </FormationsList>
          ) : (
            <NoFormationsMessage>
              <h3>{t('education:noFormationsTitle')}</h3>
              <p>{t('education:noFormationsMessage')}</p>
            </NoFormationsMessage>
          )}
        </AnimatePresence>
      </ContentWrapper>
    </FormationsContainer>
  );
};

export default FormationsPage;